import React, { useState } from "react";
import axios from "axios";
import { Sidebar } from "../components/Sidebar";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { PageHeader } from "../components/UI";

function AddHotel() {
  const navigate = useNavigate();

  // États pour les 4 champs essentiels
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [heatingType, setHeatingType] = useState("");
  const [numRooms, setNumRooms] = useState("");

  // États de validation
  const [nameValid, setNameValid] = useState(true);
  const [locationValid, setLocationValid] = useState(true);
  const [heatingTypeValid, setHeatingTypeValid] = useState(true);
  const [numRoomsValid, setNumRoomsValid] = useState(true);

  // État de chargement
  const [isSubmitting, setIsSubmitting] = useState(false);

  const auth = useAuth();
  const token = auth.user.access_token;
  const API_URL = process.env.REACT_APP_API_URL || "/api";

  const handleGoBack = () => {
    navigate(-1);
  };

  const createHotel = async (payload) => {
    const response = await axios.post(`${API_URL}/hotels`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation des champs requis
    const isNameValid = !!name.trim();
    const isLocationValid = !!location.trim();
    const isHeatingTypeValid = !!heatingType.trim();
    const isNumRoomsValid = !!numRooms && parseInt(numRooms) > 0;

    setNameValid(isNameValid);
    setLocationValid(isLocationValid);
    setHeatingTypeValid(isHeatingTypeValid);
    setNumRoomsValid(isNumRoomsValid);

    if (!isNameValid || !isLocationValid || !isHeatingTypeValid || !isNumRoomsValid) {
      alert("Veuillez remplir tous les champs obligatoires correctement.");
      return;
    }

    const payload = {
      name: name.trim(),
      num_rooms: parseInt(numRooms, 10),
      location: location.trim(),
      heating_type: heatingType.trim() || null,
    };

    try {
      setIsSubmitting(true);
      const data = await createHotel(payload);
      if (data?.success === "OK" && data?.result?.hId) {
        alert(`Hôtel créé avec succès (${data.result.roomsCreated} chambre(s)). Vous pouvez ajouter la feature LuniShower depuis la page de l'hôtel.`);
        navigate(`/hotels/${data.result.hId}`);
      } else {
        alert("Hôtel ajouté avec succès !");
        navigate("/hotels");
      }
    } catch (error) {
      alert(error.response?.data?.message || "Erreur lors de la création de l'hôtel. Veuillez réessayer.");
      console.error("Erreur lors de l'ajout de l'hôtel :", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-6 overflow-auto">
        <PageHeader title="Créer un nouvel hôtel" onGoBack={handleGoBack} showBackButton={true} />

        <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl mx-auto">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Nom de l'hôtel */}
            <div className="flex flex-col">
              <label className="mb-2 font-medium text-gray-700">
                Nom de l'hôtel <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Ex: Hôtel de la Paix"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameValid(!!e.target.value.trim());
                }}
                className={`border ${nameValid ? "border-gray-300" : "border-red-500"} rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#365644] focus:border-transparent transition-colors`}
                disabled={isSubmitting}
              />
              {!nameValid && <span className="text-red-500 text-sm mt-1">Le nom de l'hôtel est requis</span>}
            </div>

            {/* Adresse de l'hôtel */}
            <div className="flex flex-col">
              <label className="mb-2 font-medium text-gray-700">
                Adresse de l'hôtel <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Ex: 123 Rue de la République, 75001 Paris"
                value={location}
                onChange={(e) => {
                  setLocation(e.target.value);
                  setLocationValid(!!e.target.value.trim());
                }}
                className={`border ${locationValid ? "border-gray-300" : "border-red-500"} rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#365644] focus:border-transparent transition-colors`}
                disabled={isSubmitting}
              />
              {!locationValid && <span className="text-red-500 text-sm mt-1">L'adresse de l'hôtel est requise</span>}
            </div>

            {/* Type de chauffage */}
            <div className="flex flex-col">
              <label className="mb-2 font-medium text-gray-700">
                Type de chauffage <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Ex: Gaz, Électrique, Fioul, Géothermie..."
                value={heatingType}
                onChange={(e) => {
                  setHeatingType(e.target.value);
                  setHeatingTypeValid(!!e.target.value.trim());
                }}
                className={`border ${heatingTypeValid ? "border-gray-300" : "border-red-500"} rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#365644] focus:border-transparent transition-colors`}
                disabled={isSubmitting}
              />
              {!heatingTypeValid && <span className="text-red-500 text-sm mt-1">Le type de chauffage est requis</span>}
            </div>

            {/* Nombre de chambres */}
            <div className="flex flex-col">
              <label className="mb-2 font-medium text-gray-700">
                Nombre de chambres <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                max="1000"
                placeholder="Ex: 50"
                value={numRooms}
                onChange={(e) => {
                  setNumRooms(e.target.value);
                  setNumRoomsValid(!!e.target.value && parseInt(e.target.value) > 0);
                }}
                className={`border ${numRoomsValid ? "border-gray-300" : "border-red-500"} rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#365644] focus:border-transparent transition-colors`}
                disabled={isSubmitting}
              />
              {!numRoomsValid && <span className="text-red-500 text-sm mt-1">Le nombre de chambres doit être supérieur à 0</span>}
            </div>

            {/* Informations sur la création */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-blue-700">
                  <p className="font-medium mb-1">Création (table master) :</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>L'hôtel et ses chambres sont créés dans la base master (lunidata_v2)</li>
                    <li>Les chambres sont numérotées automatiquement (001, 002, …)</li>
                    <li>Pour activer LuniShower (modules, Chirpstack), utilisez le bouton sur la page de l'hôtel après création</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex justify-end space-x-4 pt-6">
              <button type="button" onClick={handleGoBack} className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" disabled={isSubmitting}>
                Annuler
              </button>
              <button type="submit" className="px-6 py-3 bg-[#365644] text-white rounded-lg shadow-md hover:bg-[#2b4737] focus:outline-none focus:ring-2 focus:ring-[#365644] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" disabled={isSubmitting}>
                {isSubmitting ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Création en cours...
                  </div>
                ) : (
                  "Créer l'hôtel"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddHotel;
